import React, { useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Quote, Target, Eye, Gem, ScrollText, Users, Workflow, TrendingUp, Sparkles,
  Pencil, ArrowRight, CalendarDays, Compass, Bold, Italic, Underline, List,
} from "lucide-react";

// Renders admin-written policy text: escapes all HTML, then re-allows only b/i/u tags.
export function renderFormatted(text) {
  const esc = (text || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  return esc.replace(/&lt;(\/?)(b|i|u)&gt;/gi, "<$1$2>").replace(/\n/g, "<br/>");
}

const ORANGE = "#e85122";

function Head({ icon: Icon, title, sub }) {
  return (
    <div className="mb-4">
      <p className="text-[11px] font-bold uppercase tracking-[0.18em] flex items-center gap-1.5" style={{ color: ORANGE }}>
        <Icon className="h-3.5 w-3.5" /> {title}
      </p>
      {sub && <p className="text-sm text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

// One "P" block of the ABOUT LIFELIFE framework.
function PBlock({ n, title, sub, children, testid }) {
  return (
    <section className="rounded-2xl border border-border bg-card p-6 sm:p-7 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid={testid}>
      <div className="flex items-start gap-4">
        <span className="h-11 w-11 rounded-2xl flex flex-col items-center justify-center text-white shrink-0 shadow-sm" style={{ background: `linear-gradient(135deg, ${ORANGE}, #f0883e)` }}>
          <span className="text-[8px] font-bold uppercase leading-none opacity-80">P{n}</span>
          <span className="font-display font-extrabold text-base leading-tight">{title[0]}</span>
        </span>
        <div className="min-w-0">
          <h4 className="font-display font-extrabold text-lg tracking-tight">{title}</h4>
          {sub && <p className="text-sm text-muted-foreground mt-0.5">{sub}</p>}
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function Chips({ items }) {
  return (
    <ul className="grid sm:grid-cols-2 gap-2">
      {(items || []).map((p) => (
        <li key={p} className="flex items-center gap-2.5 text-sm rounded-xl border border-border bg-muted/30 px-3.5 py-2.5">
          <span className="h-1.5 w-1.5 rounded-full shrink-0" style={{ background: ORANGE }} />{p}
        </li>
      ))}
    </ul>
  );
}

// ---------- Admin content editor ----------
const TEXT_KEYS = [
  ["about_tagline", "About LiveLife Tagline"],
  ["welcome_title", "Welcome Title"], ["welcome_line1", "Welcome Line 1"], ["welcome_line2", "Welcome Line 2"], ["welcome_line3", "Welcome Line 3"],
  ["ceo_message", "CEO Message", "textarea"], ["ceo_name", "CEO Name"], ["ceo_title", "CEO Title"],
  ["purpose_intro", "Purpose Intro"], ["purpose_sub", "Purpose Sub-heading"],
  ["mission", "Mission", "textarea"], ["vision", "Vision", "textarea"],
  ["values_intro", "Values Intro", "textarea"], ["values_tagline", "Values Tagline"], ["cultural_principle", "Cultural Principle"],
  ["people_tagline", "People Sub-heading"], ["directory_intro", "Directory Intro", "textarea"],
  ["process_intro", "Process Intro"], ["process_sub", "Process Sub-heading"],
  ["progress_intro", "Progress Intro"], ["progress_sub", "Progress Sub-heading"],
  ["practice_intro", "Practice Intro"], ["practice_sub", "Practice Sub-heading"],
  ["policies_intro", "Policies Intro", "textarea"], ["governance", "Governance", "textarea"], ["support", "Support", "textarea"],
  ["guiding_principles", "Guiding Principles", "textarea"],
];
const LIST_KEYS = [
  ["values", "Core Values", ["title", "text"]],
  ["policies", "Policies", ["name", "description", "body"]],
  ["policies_howto", "How To Use Policies (one per line)", "lines"],
  ["people_points", "People Points (one per line)", "lines"],
  ["process_points", "Process Points (one per line)", "lines"],
  ["progress_points", "Progress Points (one per line)", "lines"],
  ["practice_points", "Practice Points (one per line)", "lines"],
];

export function ContentEditor({ content, open, onClose, onSaved }) {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  React.useEffect(() => { if (open) setForm(JSON.parse(JSON.stringify(content || {}))); }, [open, content]);

  const setKey = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/content", { updates: form });
      toast.success("Home content updated");
      onSaved(); onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save content"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="content-editor">
        <DialogHeader><DialogTitle className="font-display">Edit Home & Company Content</DialogTitle></DialogHeader>
        <p className="text-xs text-muted-foreground">Only the Intranet Administrator can edit this content. Changes appear on Home for everyone.</p>
        <div className="space-y-4 mt-2">
          {TEXT_KEYS.map(([k, label, kind]) => (
            <div key={k}>
              <Label className="text-xs">{label}</Label>
              {kind === "textarea"
                ? <Textarea data-testid={`ce-${k}`} rows={4} value={form[k] || ""} onChange={(e) => setKey(k, e.target.value)} className="mt-1" />
                : <Input data-testid={`ce-${k}`} value={form[k] || ""} onChange={(e) => setKey(k, e.target.value)} className="mt-1" />}
            </div>
          ))}
          {LIST_KEYS.map(([k, label, fields]) => (
            <div key={k} className="rounded-xl border border-border p-3">
              <Label className="text-xs">{label}</Label>
              {fields === "lines" ? (
                <Textarea data-testid={`ce-${k}`} rows={4} className="mt-1"
                  value={(form[k] || []).join("\n")}
                  onChange={(e) => setKey(k, e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />
              ) : (
                <div className="space-y-2 mt-2">
                  {(form[k] || []).map((item, i) => (
                    <div key={i} className="rounded-lg bg-muted/40 p-2.5 space-y-1.5">
                      {fields.map((f) => (
                        <Input key={f} data-testid={`ce-${k}-${i}-${f}`} placeholder={f} value={item[f] || ""}
                          onChange={(e) => setKey(k, form[k].map((x, xi) => (xi === i ? { ...x, [f]: e.target.value } : x)))} />
                      ))}
                      <button data-testid={`ce-${k}-${i}-remove`} onClick={() => setKey(k, form[k].filter((_, xi) => xi !== i))} className="text-[11px] text-rose-600 font-semibold">Remove</button>
                    </div>
                  ))}
                  <Button type="button" variant="outline" size="sm" data-testid={`ce-${k}-add`}
                    onClick={() => setKey(k, [...(form[k] || []), Object.fromEntries(fields.map((f) => [f, ""]))])}>+ Add</Button>
                </div>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button data-testid="ce-save" onClick={save} disabled={saving}>{saving ? "Saving…" : "Save Content"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Welcome banner (top of Home, after the personal greeting) ----------
export function HomeIntro({ content, admin, onEdit }) {
  if (!content) return null;
  return (
    <section className="rounded-2xl p-6 sm:p-8 text-white shadow-md relative overflow-hidden" style={{ background: "linear-gradient(120deg, #b93a10, #E85122 55%, #f0883e)" }} data-testid="welcome-banner">
      <span className="absolute left-0 right-0 bottom-0 h-1" style={{ background: "#F6BE2C" }} />
      <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-white/80">LiveLife Education Intranet</p>
      <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-1.5">{content.welcome_title}</h3>
      <p className="text-sm text-white/90 mt-3 max-w-2xl leading-relaxed">{content.welcome_line1}</p>
      <p className="text-sm text-white/80 mt-1 max-w-2xl">{content.welcome_line2}</p>
      <p className="text-[13px] text-white/70 mt-1 max-w-2xl">{content.welcome_line3}</p>
      {admin && (
        <button data-testid="edit-home-content" onClick={onEdit} className="absolute top-4 right-4 rounded-full bg-white/15 hover:bg-white/25 p-2 transition-colors" title="Edit Home content">
          <Pencil className="h-4 w-4 text-white" />
        </button>
      )}
    </section>
  );
}

// ---------- ABOUT LIFELIFE — the 5-P company area ----------
export default function HomeCompany({ content, admin, team, upcoming, myActiveCount, myCompletedCount, onChanged }) {
  const [policy, setPolicy] = useState(null);
  const [editPolicy, setEditPolicy] = useState(null); // index of policy being edited
  const [policyDraft, setPolicyDraft] = useState({ name: "", description: "", body: "" });
  const [savingPolicy, setSavingPolicy] = useState(false);
  const [dirOpen, setDirOpen] = useState(false);
  const bodyRef = useRef(null);

  // Wrap the selected text in the policy body with a formatting tag (b/i/u).
  const wrapSelection = (tag) => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const sel = value.slice(s, e);
    const next = value.slice(0, s) + `<${tag}>${sel}</${tag}>` + value.slice(e);
    setPolicyDraft((d) => ({ ...d, body: next }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(s + tag.length + 2, e + tag.length + 2); });
  };

  // Turn the selected lines into a bullet list (• prefix per line).
  const insertBullets = () => {
    const el = bodyRef.current;
    if (!el) return;
    const { selectionStart: s, selectionEnd: e, value } = el;
    const ls = value.lastIndexOf("\n", Math.max(0, s - 1)) + 1;
    const sel = value.slice(ls, e);
    const bulleted = sel.split("\n").map((l) => (l.trim() && !l.startsWith("• ") ? "• " + l : l)).join("\n");
    const next = value.slice(0, ls) + bulleted + value.slice(e);
    setPolicyDraft((d) => ({ ...d, body: next }));
    requestAnimationFrame(() => { el.focus(); el.setSelectionRange(ls, ls + bulleted.length); });
  };

  const openPolicyEdit = (i) => {
    const p = (content.policies || [])[i];
    setPolicyDraft({ name: p.name || "", description: p.description || "", body: p.body || "" });
    setEditPolicy(i);
  };
  const savePolicy = async () => {
    setSavingPolicy(true);
    try {
      const policies = (content.policies || []).map((p, i) => (i === editPolicy ? { ...policyDraft } : p));
      await api.put("/content", { updates: { policies } });
      toast.success("Policy updated");
      setEditPolicy(null);
      if (onChanged) onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save policy"); }
    finally { setSavingPolicy(false); }
  };
  const people = useMemo(() => [...(team || [])].sort((a, b) => (a.department || "").localeCompare(b.department || "")), [team]);
  const leaders = useMemo(() => people.filter((p) => (p.roles || []).includes("ADMIN")), [people]);
  const deptGroups = useMemo(() => {
    const g = {};
    for (const p of people) { const d = p.department || "General"; g[d] = (g[d] || 0) + 1; }
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [people]);
  if (!content) return null;

  return (
    <div className="space-y-5" data-testid="home-company">
      {/* ABOUT LIFELIFE header */}
      <div className="rounded-2xl px-6 py-5 text-white shadow-md" style={{ background: "linear-gradient(135deg, #e85122, #f0883e)" }} data-testid="about-livelife">
        <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-white/85 flex items-center gap-1.5"><Compass className="h-3.5 w-3.5" /> About LiveLife</p>
        <p className="font-display text-xl sm:text-2xl font-extrabold mt-1">{content.about_tagline}</p>
        <p className="text-[13px] text-white/80 mt-1">Purpose → People → Process → Progress → Practice</p>
      </div>

      {/* P1 — PURPOSE (Mission, Vision, Values, Cultural Principle) */}
      <PBlock n={1} title="Purpose" sub={content.purpose_sub} testid="about-purpose">
        <p className="text-sm text-muted-foreground mb-4">{content.purpose_intro}</p>
        <div className="grid md:grid-cols-2 gap-4 mb-5">
          <div className="rounded-2xl border border-border bg-muted/30 p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: ORANGE }}><Target className="h-3.5 w-3.5" /> Mission</p>
            <p className="font-display text-lg font-bold leading-snug mt-2">{content.mission}</p>
          </div>
          <div className="rounded-2xl border border-border bg-muted/30 p-5">
            <p className="text-[10px] font-bold uppercase tracking-widest flex items-center gap-1.5" style={{ color: ORANGE }}><Eye className="h-3.5 w-3.5" /> Vision</p>
            <p className="text-[15px] leading-relaxed mt-2 text-foreground/90">{content.vision}</p>
          </div>
        </div>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-3xl">{content.values_intro}</p>
        <p className="font-display font-extrabold mt-3 mb-4" style={{ color: ORANGE }}>{content.values_tagline}</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {(content.values || []).map((v, i) => (
            <div key={v.title || i} className="rounded-2xl border border-border p-4 hover:border-primary/40 hover:shadow-md transition-all" data-testid={`value-${i}`}>
              <span className="inline-flex h-7 w-7 rounded-lg text-white text-xs font-extrabold items-center justify-center mb-2" style={{ background: ORANGE }}>{i + 1}</span>
              <p className="font-display font-bold text-sm uppercase tracking-wide">{v.title}</p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-xl px-4 py-3 text-sm font-semibold text-white" style={{ background: "linear-gradient(135deg, #e85122, #f0883e)" }} data-testid="cultural-principle">
          Cultural Principle — {content.cultural_principle}
        </div>
      </PBlock>

      {/* P2 — PEOPLE (Leadership, Teams, Directory, Profiles) */}
      <PBlock n={2} title="People" sub={content.people_tagline} testid="about-people">
        <Chips items={content.people_points} />
        {leaders.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Leadership</p>
            <div className="flex flex-wrap gap-2">
              {leaders.map((l) => (
                <span key={l.user_id} className="inline-flex items-center gap-2 rounded-full border border-border bg-muted/30 pl-1 pr-3 py-1 text-xs font-semibold" data-testid={`leader-${l.user_id}`}>
                  <span className="h-6 w-6 rounded-full flex items-center justify-center text-white text-[10px] font-bold" style={{ background: ORANGE }}>
                    {(l.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                  </span>
                  {l.name}{l.designation ? <span className="text-muted-foreground font-normal">· {l.designation}</span> : null}
                </span>
              ))}
            </div>
          </div>
        )}
        {deptGroups.length > 0 && (
          <div className="mt-4">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-2">Our Teams</p>
            <div className="flex flex-wrap gap-2">
              {deptGroups.map(([d, n]) => (
                <span key={d} className="rounded-full border border-border px-3 py-1 text-xs text-muted-foreground">{d} <strong className="text-foreground">{n}</strong></span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4 rounded-xl border border-border bg-muted/30 p-4 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-[13px] text-muted-foreground max-w-xl leading-relaxed">{content.directory_intro}</p>
          <Button data-testid="open-directory" variant="outline" className="gap-1.5 shrink-0" onClick={() => setDirOpen(true)}>
            <Users className="h-4 w-4" /> Employee Directory
          </Button>
        </div>
      </PBlock>

      {/* P3 — PROCESS */}
      <PBlock n={3} title="Process" sub={content.process_sub} testid="about-process">
        <Chips items={content.process_points} />
      </PBlock>

      {/* P4 — PROGRESS */}
      <PBlock n={4} title="Progress" sub={content.progress_sub} testid="about-progress">
        <Chips items={content.progress_points} />
        <div className="grid grid-cols-2 gap-2 mt-4 max-w-sm">
          <div className="rounded-xl bg-muted/40 px-3 py-2.5 text-center"><p className="font-display font-extrabold text-xl tabular-nums">{myActiveCount}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">My Active Work</p></div>
          <div className="rounded-xl bg-muted/40 px-3 py-2.5 text-center"><p className="font-display font-extrabold text-xl tabular-nums text-emerald-600">{myCompletedCount}</p><p className="text-[10px] uppercase tracking-wider text-muted-foreground">Completed</p></div>
        </div>
        {(upcoming || []).length > 0 && (
          <div className="mt-3">
            <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">Upcoming</p>
            {upcoming.slice(0, 2).map((e) => (
              <p key={e.ann_id} className="text-xs flex items-center gap-1.5 mt-1"><CalendarDays className="h-3 w-3 shrink-0" style={{ color: ORANGE }} />{e.title}{e.event_date ? ` · ${fmtDate(e.event_date)}` : ""}</p>
            ))}
          </div>
        )}
      </PBlock>

      {/* P5 — PRACTICE */}
      <PBlock n={5} title="Practice" sub={content.practice_sub} testid="about-practice">
        <Chips items={content.practice_points} />
      </PBlock>

      {/* PEOPLE POLICIES (existing content preserved) */}
      <section className="rounded-2xl border border-border bg-card p-6 sm:p-7 shadow-[0_1px_3px_rgba(16,24,40,0.05)]" data-testid="people-policies">
        <Head icon={ScrollText} title="People Policies" />
        <p className="text-sm text-muted-foreground whitespace-pre-line max-w-3xl leading-relaxed">{content.policies_intro}</p>
        <div className="mt-3 rounded-xl border border-border bg-muted/30 p-4">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mb-1.5">How to use these policies</p>
          <ul className="text-[13px] space-y-1">
            {(content.policies_howto || []).map((h) => <li key={h} className="flex gap-2"><span className="mt-2 h-1 w-1 rounded-full shrink-0" style={{ background: ORANGE }} />{h}</li>)}
          </ul>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-4">
          {(content.policies || []).map((p, i) => (
            <button key={p.name || i} data-testid={`policy-${i}`} onClick={() => setPolicy({ ...p, _index: i })}
              className="text-left rounded-2xl border border-border p-4 hover:border-primary/40 hover:shadow-md transition-all group">
              <p className="font-display font-bold text-sm flex items-center justify-between gap-2">{p.name}
                <ArrowRight className="h-3.5 w-3.5 text-muted-foreground group-hover:translate-x-0.5 transition-transform shrink-0" /></p>
              <p className="text-xs text-muted-foreground mt-1.5 leading-relaxed">{p.description}</p>
            </button>
          ))}
        </div>
        <div className="grid md:grid-cols-3 gap-3 mt-4 text-[13px]">
          <div className="rounded-xl border border-border bg-muted/30 p-4"><p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: ORANGE }}>Our Guiding Principles</p><p className="text-muted-foreground leading-relaxed">{content.guiding_principles}</p></div>
          <div className="rounded-xl border border-border bg-muted/30 p-4"><p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: ORANGE }}>Governance</p><p className="text-muted-foreground leading-relaxed">{content.governance}</p></div>
          <div className="rounded-xl border border-border bg-muted/30 p-4"><p className="text-[10px] font-bold uppercase tracking-widest mb-1" style={{ color: ORANGE }}>Support</p><p className="text-muted-foreground leading-relaxed">{content.support}</p></div>
        </div>
      </section>

      {/* CEO MESSAGE (kept inside About LiveLife, not in Important Information) */}
      <section className="rounded-2xl border border-border bg-card p-6 sm:p-8 shadow-[0_1px_3px_rgba(16,24,40,0.05)] relative" data-testid="ceo-message">
        <Quote className="h-8 w-8 absolute top-5 right-6 opacity-10" style={{ color: ORANGE }} />
        <Head icon={Quote} title="A Message from the CEO" />
        <p className="text-[15px] leading-relaxed text-foreground/90 whitespace-pre-line max-w-3xl font-serif italic">{content.ceo_message}</p>
        <p className="mt-5 font-display font-extrabold">— {content.ceo_name}</p>
        <p className="text-xs text-muted-foreground">{content.ceo_title}</p>
      </section>

      {/* Policy detail modal */}
      <Dialog open={!!policy} onOpenChange={(o) => !o && setPolicy(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[80vh] overflow-y-auto" data-testid="policy-modal">
          {policy && (
            <>
              <DialogHeader><DialogTitle className="font-display text-xl">{policy.name}</DialogTitle></DialogHeader>
              <p className="text-sm text-muted-foreground leading-relaxed">{policy.description}</p>
              {policy.body && <p className="text-sm mt-3 leading-relaxed" dangerouslySetInnerHTML={{ __html: renderFormatted(policy.body) }} />}
              {!policy.body && <p className="text-xs text-muted-foreground mt-3 italic">Detailed policy text will be published here by the Intranet Administrator.</p>}
              <p className="text-[11px] text-muted-foreground mt-4 border-t border-border pt-3">{content.support}</p>
              {admin && (
                <Button data-testid="edit-policy-button" size="sm" variant="outline" className="mt-3 gap-1.5" onClick={() => { setPolicy(null); openPolicyEdit(policy._index); }}>
                  <Pencil className="h-3.5 w-3.5" /> Edit Policy
                </Button>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin policy editor */}
      <Dialog open={editPolicy !== null} onOpenChange={(o) => !o && setEditPolicy(null)}>
        <DialogContent aria-describedby={undefined} className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="policy-editor">
          <DialogHeader><DialogTitle className="font-display">Edit Policy</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label className="text-xs">Policy Name</Label>
              <Input data-testid="policy-edit-name" value={policyDraft.name} onChange={(e) => setPolicyDraft({ ...policyDraft, name: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Short Description (shown on the card)</Label>
              <Textarea data-testid="policy-edit-description" rows={2} value={policyDraft.description} onChange={(e) => setPolicyDraft({ ...policyDraft, description: e.target.value })} className="mt-1" /></div>
            <div><Label className="text-xs">Policy Content (shown when opened)</Label>
              <div className="flex gap-1 mt-1 mb-1.5" data-testid="policy-format-toolbar">
                {[["b", Bold, "Bold", "policy-edit-bold-btn"], ["i", Italic, "Italic", "policy-edit-italic-btn"], ["u", Underline, "Underline", "policy-edit-underline-btn"]].map(([tag, Icon, title, tid]) => (
                  <Button key={tag} type="button" data-testid={tid} size="sm" variant="outline" className="h-7 w-7 p-0" title={title}
                    onMouseDown={(e) => e.preventDefault()} onClick={() => wrapSelection(tag)}><Icon className="h-3.5 w-3.5" /></Button>
                ))}
                <Button type="button" data-testid="policy-edit-bullet-btn" size="sm" variant="outline" className="h-7 w-7 p-0" title="Bullet list"
                  onMouseDown={(e) => e.preventDefault()} onClick={insertBullets}><List className="h-3.5 w-3.5" /></Button>
                <span className="text-[10px] text-muted-foreground self-center ml-1">Select text, then click to format</span>
              </div>
              <Textarea ref={bodyRef} data-testid="policy-edit-body" rows={8} value={policyDraft.body} onChange={(e) => setPolicyDraft({ ...policyDraft, body: e.target.value })} className="mt-1" placeholder="Full policy text…" /></div>
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button variant="outline" onClick={() => setEditPolicy(null)}>Cancel</Button>
            <Button data-testid="policy-edit-save" onClick={savePolicy} disabled={savingPolicy}>{savingPolicy ? "Saving…" : "Save Policy"}</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Employee Directory modal */}
      <Dialog open={dirOpen} onOpenChange={setDirOpen}>
        <DialogContent aria-describedby={undefined} className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="directory-modal">
          <DialogHeader><DialogTitle className="font-display text-xl">Employee Directory</DialogTitle></DialogHeader>
          <p className="text-xs text-muted-foreground">{content.directory_intro}</p>
          <div className="mt-3 space-y-1.5">
            {people.map((p) => (
              <div key={p.user_id} className="flex items-center gap-3 rounded-xl border border-border px-3.5 py-2.5" data-testid={`dir-${p.user_id}`}>
                <span className="h-9 w-9 rounded-xl flex items-center justify-center text-white font-display font-bold text-sm shrink-0" style={{ background: ORANGE }}>
                  {(p.name || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase()}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold truncate">{p.name}{p.employee_id ? <span className="ml-2 font-mono text-[11px] text-muted-foreground">{p.employee_id}</span> : null}</p>
                  <p className="text-xs text-muted-foreground truncate">{p.designation || "—"} · {p.department || "—"}</p>
                </div>
                <p className="text-[11px] text-muted-foreground hidden sm:block truncate">{p.email}</p>
              </div>
            ))}
            {people.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">Directory is empty.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
